import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { Option } from "../../../common/types/Option";
import { svodDohFilterOptions, allSvodDohIncomeClassificators } from "../report-svod-doh";
import { SvodDohFilterSettingsForm } from "../settings-form/SvodDohFilterSettingsForm";
import { SvodDohFilterSettings } from "../settings/SvodDohFilterSettings";
import { ReportSvodDohTableDataRequest } from "../ReportSvodDohTableDataRequest";
import { BudgetType, MutuallyRedeemingLevel, Segmentation } from '../../../common/types/roots/fields';
import { Draggable } from "@/modules/budget/monitoring/reports-constructor/common/types/Draggable";

export class SvodDohFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(svodDohFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        if (settings.reportName !== 'SVOD-DOH') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'SVOD-DOH') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        const regionOption: Option<string> | null = cloneDeep(options.regions.find(it => it.value === settings.region) ?? null)

        let byFormOption = options.byForms.find(it => it.value === settings.byForm)
        if (!byFormOption) {
            throw new Error('error_not_found_by_form')
        }
        byFormOption = { ...byFormOption }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let segmentationOptions = options.segmentations
        if (!segmentationOptions) {
            throw new Error('error_not_found_segmentation_option')
        }
        segmentationOptions = cloneDeep(segmentationOptions)

        let isRoundUp = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }

        const statuses = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const budgetTypes: Option<BudgetType>[] = []
        settings.budgetTypes.forEach(budget => {
            const budgetTypeOption = options.budgetTypes.find(item => item.value === budget)!
            budgetTypes.push({ ...budgetTypeOption })
        })

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        const mutuallyRedeemingLevelOptions: Option<MutuallyRedeemingLevel>[] = []
        settings.mutuallyRedeemingLevels.forEach(level => {
            const option = options.mutuallyRedeemingLevels.find(it => it.value === level)!
            mutuallyRedeemingLevelOptions.push({ ...option })
        })

        const form: SvodDohFilterSettingsForm = {
            key: 'SVOD-DOH',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            measureUnit: measureUnitOption,
            mutuallyRedeeming: mutuallyRedeemingOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo ?? null,
            byForm: byFormOption,
            region: regionOption,
            date: new Date(settings.date),
            statuses: statuses,
            incomes: cloneDeep(settings.incomes),
            budgetTypes: budgetTypes,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            onlyCurrentYearKbk: settings.onlyCurrentYearKbk ?? true,
            treeView: settings.treeView ?? true,
            fillKbk: settings.fillKbk ?? false,
            segmentations: segmentationOptions,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'SVOD-DOH') {
            throw new Error('error_type_filter_settingsForm')
        }
        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }
        if (form.mutuallyRedeeming == null) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (form.region == null) {
            throw new Error('error_input_region')
        }
        if (form.byForm == null) {
            throw new Error('error_input_by_form')
        }
        if (!form.date) {
            throw new Error('error_input_date')
        }
        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }
        if (!form.segmentations) {
            throw new Error('error_input_segmentation')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: SvodDohFilterSettings = {
            reportName: 'SVOD-DOH',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            budgetTypes: form.budgetTypes.map(it => it.value),
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            byForm: form.byForm.value,
            region: form.region.value,
            date: strDate,
            statuses: form.statuses.map(status => status.value),
            incomes: cloneDeep(form.incomes),
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels?.map(it => it.value) ?? [],
            onlyCurrentYearKbk: form.onlyCurrentYearKbk,
            treeView: form.treeView,
            fillKbk: form.fillKbk,
            segmentations: form.segmentations,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(svodDohFilterOptions)

        const emptyForm: SvodDohFilterSettingsForm = {
            key: 'SVOD-DOH',
            reportName: options.reportNames.find(it => it.value === 'SVOD-DOH')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value === 'IISK')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            mutuallyRedeeming: options.mutuallyRedeeming.find(it => it.value === 'WITHOUT')!,
            byForm: options.byForms.find(it => it.value === 'SVOD-DOH')!,
            roundUp: false,
            roundUpTo: null,
            budgetTypes: [],
            mutuallyRedeemingLevels: [],
            region: null,
            date: null,
            statuses: [options.statuses.find(it => it.value === '39')!],
            incomes: cloneDeep(allSvodDohIncomeClassificators),
            onlyCurrentYearKbk: true,
            treeView: true,
            fillKbk: false,
            segmentations: cloneDeep(options.segmentations),
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        if (form.key !== 'SVOD-DOH') {
            throw new Error('error_type_filter_settingsForm')
        }

        const params = this.constructDataRequest(form, userId)

        const response = await fetch('/api-py/monitoring/reports-constructor/svod-doh',
            {
                method: 'POST',
                cache: 'no-cache',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseData: any = await response.json()
        const tableData: any = responseData

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const cutness: string[] = form.segmentations?.filter(v => v.active).map(v => v.value) ?? []
        const strDate = moment(form.date).format('YYYY-MM-DD')

        const journalParams: any = {
            reportName: "SVOD-DOH",

            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,

            region: form.region!.value,
            report_date: strDate,
            date: strDate,
            measure_unit: form.measureUnit!.value,
            budget_types: form.budgetTypes.map(it => it.value),
            rows: tableData,
            incomes: form.incomes,
            statuses: form.statuses.map(it => it.value),
            dataSource: form.dataSource.value,
            cutness: cutness,
            fillKbk: form.fillKbk,
            treeView: form.treeView,
            lang: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }

    public constructDataRequest(form: FilterSettingsForm, userId: string): ReportSvodDohTableDataRequest {
        if (form.key !== 'SVOD-DOH') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (form.measureUnit == null) {
            throw new Error('error_input_measure_unit')
        }

        if (form.budgetTypes.length === 0) {
            throw new Error('error_select_budget')
        }

        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] = form.segmentations.filter(v => v.active).map(v => v.value)

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const tempIncomes = form.incomes
            .map(it => ({ key: it.key, selected: it.selected, active: it.active }))

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: FilterSettings = this.formToSettings(cloneDeep(form))

        const params: ReportSvodDohTableDataRequest = {
            user_id: userId,
            incomes: tempIncomes,
            date: strDate,
            region: form.region.value,
            vzk: form.mutuallyRedeeming?.value,
            measureUnit: form.measureUnit.value,
            roundTo: roundUpTo,
            statuses: form.statuses.map(status => status.value),
            budget_types: form.budgetTypes.map(it => it.value),
            mutually_redeeming_levels: form.mutuallyRedeemingLevels.map(it => it.value),
            dataSource: form.dataSource.value,
            onlyCurrentYearKbk: form.onlyCurrentYearKbk,
            treeView: form.treeView,
            fillKbk: form.fillKbk,
            cutness: cutness,
            lang: i18nService.locale,
            filter_settings: settings,
        }

        return params
    }
}
